import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cx, sortCx } from '../../../utils/cx';

const styles = sortCx({
  base: [
    'group inline-flex w-full select-none items-center gap-3 rounded-xl px-3.5 text-left font-medium transition duration-150 ease-linear',
    'outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2',
    'disabled:cursor-not-allowed disabled:text-fg-disabled disabled:opacity-60'
  ].join(' '),
  tone: {
    neutral: [
      'text-secondary hover:bg-primary_hover hover:text-secondary_hover',
      'data-[active=true]:bg-primary_alt data-[active=true]:text-secondary',
      'data-[active=true]:ring-1 data-[active=true]:ring-primary data-[active=true]:shadow-xs'
    ].join(' '),
    danger: [
      'text-error-primary hover:bg-error-primary/10 hover:text-error-primary',
      'data-[active=true]:bg-error-primary data-[active=true]:text-white',
      'data-[active=true]:ring-1 data-[active=true]:ring-error_subtle data-[active=true]:shadow-xs'
    ].join(' ')
  },
  size: {
    md: 'gap-3 py-2.5 text-sm',
    lg: 'gap-3.5 py-3 text-md'
  },
  inset: {
    true: 'pl-9',
    false: ''
  }
});

export type MenuItemTone = keyof typeof styles.tone;
export type MenuItemSize = keyof typeof styles.size;

export interface MenuItemProps extends ComponentPropsWithoutRef<'button'> {
  tone?: MenuItemTone;
  size?: MenuItemSize;
  description?: ReactNode;
  shortcut?: string | string[];
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  active?: boolean;
  inset?: boolean;
}

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  function MenuItem(
    {
      tone = 'neutral',
      size = 'md',
      description,
      shortcut,
      leadingIcon,
      trailingIcon,
      active = false,
      inset = false,
      className,
      children,
      type = 'button',
      ...props
    },
    ref
  ) {
    const shortcutTokens = Array.isArray(shortcut)
      ? shortcut
      : shortcut
        ? [shortcut]
        : [];

    return (
      <button
        ref={ref}
        type={type}
        data-tone={tone}
        data-size={size}
        data-active={active ? true : undefined}
        data-inset={inset ? true : undefined}
        className={cx(
          styles.base,
          styles.tone[tone],
          styles.size[size],
          inset && styles.inset.true,
          className
        )}
        {...props}
      >
        {leadingIcon && (
          <span className="text-current" data-icon="leading">
            {leadingIcon}
          </span>
        )}

        <span className="flex flex-1 flex-col gap-0.5 overflow-hidden">
          <span
            className="truncate text-sm font-semibold text-current"
            data-slot="label"
          >
            {children}
          </span>
          {description && (
            <span
              className="truncate text-xs text-tertiary"
              data-slot="description"
            >
              {description}
            </span>
          )}
        </span>

        {(shortcutTokens.length > 0 || trailingIcon) && (
          <span
            className="ml-auto inline-flex items-center gap-2"
            data-slot="meta"
          >
            {shortcutTokens.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[0.75rem] font-semibold uppercase tracking-wide text-tertiary">
                {shortcutTokens.map((token, index) => (
                  <kbd
                    key={`${token}-${index}`}
                    className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-secondary ring-1 ring-inset ring-secondary"
                  >
                    {token}
                  </kbd>
                ))}
              </span>
            )}
            {trailingIcon && (
              <span className="text-current" data-icon="trailing">
                {trailingIcon}
              </span>
            )}
          </span>
        )}
      </button>
    );
  }
);

MenuItem.displayName = 'UntitledUI.MenuItem';
